/* Cap of the page extract that report carries. Half of what the server accepts (WidgetHostPageText.
   MaxLength — the two must stay in step). Raised from 2,000 on 2026-09-27: many pages open with a slogan
   and name what they offer only further down, and a question is written once per page and then stored,
   so the extra text costs a fraction of a cent once. Kept well under the 64 KiB a keepalive request may
   carry, which it stays below even when every character takes four bytes. */
export const pageTextMaxLength = 8000;
/* Ceiling on the nodes read for it, so the extract of a ten-thousand-row table costs a bounded walk. */
export const pageTextMaxBlocks = 400;
/* The outline the chat reads on demand (WidgetReadHostPage): every heading, one paragraph under each,
   cut to what a heading's opening needs. Thirty headings cover a long article's structure; past that
   the page is a catalogue, and its outline says so by ending. The total mirrors
   WidgetHostPageText.ChatMaxLength — the two must stay in step. */
export const pageOutlineMaxHeadings = 30;
export const pageOutlineMaxParagraphLength = 300;
export const pageOutlineMaxLength = 12000;
/* Grace before the page is read. The report also fires on client-side route changes, where the new
   route is announced BEFORE it renders — read immediately and a framework hands over the outgoing
   page's text under the incoming page's path. Long enough for a render, short enough that a visitor
   who bounces still leaves the question behind for the next one. */
export const pageCoverageDelayMs = 900;
/* How the corner waits for a question that is being written right now (see pollPageMessage): how often
   it asks, and how long it is willing to hold the generic bubble back before deciding that nothing is
   coming. Twelve times two seconds covers a model call comfortably; past that the visitor has been
   looking at an empty corner long enough, and the site-wide bubble is better than none. */
export const pageCoveragePollMs = 2000;
export const pageCoveragePollAttempts = 12;
/* How often a scheduled bubble re-checks while the corner is still waiting. Shorter than the poll, so
   an arriving question is offered in the same breath it lands rather than a poll interval later. */
export const pageCoverageRecheckMs = 400;

/* What the launcher wears while the contractor has picked no colour of their own: LeadWin's violet, the
   very hex the server serves as its default (LeadWinWidgetOptions.LeadWinAccentHex — keep the two in
   step). It used to be a near-black, which was a colour nobody chose: on a white page it read as a stray
   UI chrome rather than as an agent, and on a dark one the circle sank into the page and the
   only thing left of the launcher was its white sparkles. The violet clears 3:1 against both grounds, so
   one value serves both looks — which is also why the dark look gets no second constant here. */
export const defaultAccent = '#7a2bff';
/* The panel outline on a dark page without a contractor colour to wear — light enough to be an edge
   where the drop shadow is not one. */
export const defaultPanelBorder = '#8b8f99';
export const configTimeoutMs = 1500;
/* Floor between two re-reads of the config (see revalidateConfig). Long enough that alt-tabbing back
   and forth cannot turn into a request per keystroke, short enough for the loop a contractor actually
   works in: save, look at the website, change something, look again. A longer floor made the second
   look show the first change — which reads exactly like the bug this exists to fix. The ask is a
   conditional GET that answers 304 whenever nothing moved. */
export const configRevalidateMs = 5000;
export const readyTimeoutMs = 8000;
export const retryPollIntervalMs = 2500;
export const maxRetryPolls = 240; // ~10 min of recovery polling per outage — never polls a dead server forever.

/* Proactive nudges: the clickable bubbles that pop over the launcher while the chat is still closed.
 * Server-driven — the config endpoint decides whether there are any (sales mode, switched on, texts
 * configured); this file only renders and times them. */
export const maxNudges = 1;             // Mirrors LeadWinAgent.MaxNudges; the server caps it too.
export const nudgeMaxTextLength = 200;  // Defense in depth over the domain's own 60-char cap.

/* Where a surface may appear (see url-rules.js). Both mirror WidgetUrlRules; the server caps them too,
 * so these only catch a hand-edited payload. */
export const maxUrlRules = 20;
export const urlRuleValueMaxLength = 200;

/* How long the "hide these for good" offer stays after a visitor dismissed a bubble — or the launcher's
 * trust corner, which makes the same offer in the same shape. Long enough to be read and reached, short
 * enough that ignoring it is a complete answer — it takes itself away, so saying no costs nothing. */
export const optOutOfferMs = 6000;

/* How long the confirmation stays before the stack goes. Just long enough to see that the tap landed. */
export const optOutDoneMs = 1400;

/* How long the bubbles' confirmation offers to take the choice back. Longer than the confirmation it
 * replaces, because it asks for a decision rather than reporting one: the chip is a one-tap, permanent,
 * per-device "never again" sitting right where a visitor aims to dismiss a bubble, so the tap that lands
 * on it by accident needs a window wide enough to notice and reach. The trust corner makes no such offer —
 * its opt-out is per group and already reversible by name in the widget's own panel. */
export const optOutUndoMs = 5000;
export const minNudgeDelayMs = 1000;
export const maxNudgeDelayMs = 300000;
/* Mirrors LeadWinAgent.DefaultNudgeDelaySeconds (5 s since 2026-10-11, 12 s before). */
export const defaultNudgeDelayMs = 5000;

/* Page-specific proactive messages: the LeadWin agent's own question for one page of the site, matched
 * against location.pathname client-side. Where a page matches, its question takes the bubble slot the
 * generic nudge would use; the click opens the chat, which greets with that question. */
/* Defensive only, like the character caps beside it — never a policy. How many pages carry a question is
   the plan's decision (WidgetEntitlement.MaxAutoPageMessages, plus its knowledge pages), and this number
   sits above anything the largest plan can legitimately send. It used to mirror the knowledge-page cap of
   the largest plan, which silently became a functional limit the moment automatic coverage started
   feeding the same list: everything past the twentieth path was dropped here while the manage panel went
   on showing those pages as covered. Raise it WITH the plans, never behind them. */
export const maxPageMessages = 600;

/* Announcements/campaigns: the cards (or one deliberate modal) the manage panel's campaign section
 * schedules onto the host website. Server-driven like the nudges — only currently active campaigns
 * arrive at all; this file renders them, matches their page rules and caps their frequency. */
export const maxAnnouncements = 20;            // Mirrors LeadWinAgentAnnouncement.MaxPerWidget.
export const announcementTitleMaxLength = 200; // Defense in depth over the domain's own 60-char cap.
export const announcementBodyMaxLength = 600;  // Defense in depth over the domain's own 300-char cap.
export const announcementBadgeMaxLength = 60;      // Defense in depth over the domain's own 30-char cap.
export const announcementHighlightsMaxCount = 6;   // Mirrors LeadWinAgentAnnouncement.HighlightsMaxLines.
export const announcementHighlightMaxLength = 60;  // Defense in depth over the domain's own 40-char cap.
export const announcementKinds = ['news', 'teaser', 'offer'];

/* The campaign's own waiting time, in the same milliseconds nudgeDelayMs travels in. Mirrors
 * LeadWinAgentAnnouncement.Min/Max/DefaultDelaySeconds; a payload outside them (or one that predates the
 * field) falls back to the campaigns' own default. That was the bubbles' default until the bubbles went
 * down to five seconds on 2026-10-11; the campaigns kept their twelve. */
export const defaultAnnouncementDelayMs = 12000;
export const minAnnouncementDelayMs = 5000;
export const maxAnnouncementDelayMs = 300000;
export const announcementLayouts = ['classic', 'accent', 'minimal', 'spotlight'];

/* How much room a campaign's dialog takes, written onto it as data-size — three named steps rather than
 * a width, because a number out of a payload has no business in a style rule on somebody else's page.
 * Mirrors WidgetAnnouncementModalSize; anything else, an older payload included, renders standard. */
export const announcementModalSizes = ['compact', 'standard', 'large'];

/* The campaign's own colours end up in CSS custom properties on somebody else's page, so they are
 * accepted in exactly one shape — the same strict #rrggbb the domain stores and the widget's own accent
 * already lives by. Anything else is dropped and the stylesheet's per-kind hue stands. */
export const announcementColorPattern = /^#[0-9a-f]{6}$/i;

/* The launcher's rotating cards — the trust surface's other form, ticked per widget in the manage
 * panel: quotes from the provider's reviews, tiles of their projects, cards of their earned seals,
 * cards of the awards they wrote themselves, the "Empfehlungssiegel" and the "Bewertungssiegel", in
 * any combination. Server-resolved and already trimmed; the caps here are defense in depth, like the
 * campaign ones above. */
/* The quotes arrive as a POOL and a deck draws from it, so these two are different numbers: the payload
 * is cached per site and cannot be picked per visitor, while the corner must not quote the same two
 * customers on every page. The draw happens here instead — once per page load, the visitor's own side of
 * the platform first (see drawTrustReviews). */
export const maxTrustReviewPool = 24;            // Mirrors WidgetTrustReviewsProvider.MaxReviews.
export const maxTrustReviews = 10;               // Mirrors WidgetTrustDeck.MaxReviewCards.
export const maxTrustProjects = 10;              // Mirrors WidgetTrustProjectsProvider.MaxProjects.
export const maxTrustAwards = 10;                // Mirrors WidgetLauncherTrustResolver.MaxAwards.
export const trustReviewQuoteMaxLength = 400;    // Well over the query's own 180-char trim.
export const trustReviewAuthorMaxLength = 80;
export const trustReviewSourceMaxLength = 40;    // "Trustpilot", "Google", … — a platform name, not a sentence.
export const trustProjectTitleMaxLength = 120;
export const trustProjectClientMaxLength = 80;
export const trustAwardNameMaxLength = 120;      // "Top-Anbieter für …" plus the longest directory name.
export const trustSealLineMaxLength = 80;        // "Top-Bewertung", "4,8", "27 Bewertungen", the source line.

/* The deck the corner rotates through holds everything the server sent: ten of each form where a widget
 * shows several, because a choice that promises quotes AND projects AND seals has to show all of each —
 * a shared cap would silently drop the tail of one of them. The per-form caps above are what bounds it;
 * this is the defense-in-depth ceiling over their sum. The two single-card seals add one apiece. */
export const maxTrustCards = maxTrustReviews + maxTrustProjects + (maxTrustAwards * 2) + 2;

/* Which of the board's four shapes a deck card is drawn with. Six kinds, four shapes: the three seal
 * forms share one, because a platform verdict, a provider's own award and the "Empfehlungssiegel" are
 * one line plus the line that says who stands behind it — and telling them apart is that second line's
 * job, not a second layout's. */
export const trustCardShapes = {
    review: 'review',
    project: 'project',
    socialpost: 'social',
    award: 'seal',
    ownaward: 'seal',
    toprated: 'seal',
    ratingseal: 'ratingseal'
};

/* How many posts one deck deals, and how long their line may be. A post card is a picture and one line,
 * like a project tile — but the corner is saying "somebody is publishing here", and the fifth post says
 * that as well as the fiftieth would. */
export const maxTrustSocialPosts = 5;
export const trustSocialTitleMaxLength = 90;
export const trustSocialNetworkMaxLength = 20;
export const trustSocialAuthorMaxLength = 60;

/* The networks the card knows how to dress itself as. A closed list on purpose: the slug picks a brand
 * mark and an accent colour that are DRAWN in the loader, so one that arrived from a newer server than
 * the cached script would otherwise pick nothing and leave a hole where the mark belongs. An unknown
 * slug falls back to no mark at all, and the card still reads. */
export const trustSocialNetworks = ['linkedin', 'youtube', 'tiktok', 'instagram', 'x'];

/* The cited key of a post ("li-7123456789012345678"). Mirrors WidgetSocialPostKeys.MaxLength: the widget
 * refuses anything longer, so a payload carrying one has nothing to look up either way. */
export const trustSocialKeyMaxLength = 64;

/* The three groups a visitor may switch the corner's proof off in, and which of the six card forms
 * belongs to each. Deliberately coarser than the forms: a visitor closing a card answers "not this kind
 * of thing", and asking them the same question six times over would be its own nuisance. The rating
 * seal counts as a REVIEW rather than as a seal — it is the average of the reviews and it opens the
 * review list, so leaving it in the seals would break the promise "Bewertungen ausblenden" makes. */
export const trustOptOutGroups = ['reviews', 'projects', 'seals', 'social'];

export const trustCardGroups = {
    review: 'reviews',
    ratingseal: 'reviews',
    project: 'projects',
    socialpost: 'social',
    award: 'seals',
    ownaward: 'seals',
    toprated: 'seals'
};

/* How the two launcher surfaces name themselves to the beacon: the card FORM for a trust card, the
 * bubble id the config payload served for a bubble. Mirrors WidgetLauncherSurfaceKeys (Feedbax.Domain),
 * which is what the ingest validator reads them back with — the two must stay in step. */
export const trustEventKeyPrefix = 'tr:';
export const nudgeEventKeyPrefix = 'nu:';

/* And the mark each kind hangs in the card's top-right corner. Decorative throughout (the card's own
 * lines carry the content), which is why a kind that is missing here simply gets none. */
export const trustCardMarks = {
    award: '🏆',
    ownaward: '🏅',
    toprated: '⭐'
};

/* The four languages the Top-Rated shield artwork exists in. A closed set on purpose: the value picks
 * a stylesheet background, and a language without a file would render an empty hole. Mirrors
 * WidgetTrustSealCard in Feedbax.Web. */
export const trustBadgeLanguages = ['de', 'en', 'fr', 'nl'];

/* The one shape a review key may have. Validated before it is put in a frame URL: everything travelling
 * from this file into the widget page is an id, and an id that is not one has no business being sent. */
export const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/* How long one trust card stands. A setting now (LeadWinAgent.TrustRotationSeconds), so these
 * three only bound what arrives: the default catches a payload that predates the field, the floor keeps
 * a stored zero from turning somebody's corner into a strobe, and the ceiling keeps a typo from
 * freezing it. The numbers mirror the domain's — a second answer to a stored setting is a bug waiting
 * for the day the two disagree. */
export const defaultTrustRotateMs = 5000;      // Long enough to read a three-line quote, short enough to see two.
export const minTrustRotateMs = 3000;
export const maxTrustRotateMs = 10000;

/* Screens the JS API may open, by the names the widget page's ?panel= parameter and the showpanel
 * message understand (mirrors WebConstants.Widget — the two vocabularies must stay in step). */
export const apiScreens = ['lead', 'booking', 'helpcenter', 'profile'];

/* Mirrors WidgetPageQueryKeys.MaxKeys / KeyMaxLength: the query parameters a provider may name as naming
 * a screen. Small on purpose — every one of them multiplies the pages a widget covers, and each covered
 * page is a model call against the daily quota. */
export const maxPageQueryKeys = 5;
export const pageQueryKeyMaxLength = 40;

/* ---- The loader's outward events: one family, fixed here so a later member fits beside the first ----
 *
 * The loader speaks to the host page through CustomEvents and through nothing else: no subscribe API,
 * nothing to register, nothing to unregister, no handler lifetime to own. Today there is exactly one
 * member (the panel opened or closed); a second one — a lead came in, an appointment was booked, the chat
 * opened for the first time — has to look like the first, so the shape is written down now and every
 * later member follows it:
 *
 *   Name     'leadwin:<topic>' — the prefix below plus one lowercase topic word ('panel', later perhaps
 *            'lead', 'booking'). No nesting, no verbs: the topic names WHAT changed, the detail says how.
 *   Payload  always an object in event.detail, never a bare value — { open: true }, not true — so a field
 *            can be added later without changing the type of what a listener already reads. Every field
 *            is a promise kept forever: adding one is easy, taking one away is not, so payloads stay tiny.
 *   When     on a real change only, never on every command. Three 'open' calls against an open panel raise
 *            one event; a listener that toggles a class must be able to trust that.
 *   Where    dispatched on document with bubbles: true, so a listener on window catches it too. Never
 *            cancelable: these are reports of something that already happened, not requests for leave.
 *   Rule     the loader REPORTS, it does not ACT. It never dims, moves, recolours or hides anything of the
 *            host page's own — whose page it is decides what happens on it. Feedbax' own app dims the
 *            drawers the panel covers by listening to this event (wwwroot/js/leadwin.js), exactly the way
 *            any other provider would, and a loader that did that by itself would be one they could not
 *            embed. This is what keeps the loader open to every provider (Platform Mode).
 *
 * Once the product is live, every name here is a promise kept for as long as a single page listens to
 * it — a rename then means raising both names, not swapping one for the other. */
export const eventNamePrefix = 'leadwin:';

/* The panel opened, or it closed — the family's first member, and today its only one. A host page that
 * wants to react to an open chat (dim what the panel covers, pause a video, park a sticky bar) needs to
 * know THAT, and nothing beyond it.
 * detail: { open: boolean }. */
export const panelStateEventName = eventNamePrefix + 'panel';
